import { BRAND_HANDLE, BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { getBaseLanguage } from "@/lib/i18n";
// Amplitude tracking removed
import { Link } from "react-router-dom";
import { Instagram, Youtube } from "lucide-react";
import profileImage from "@/assets/profile-delfu.jpg";

const socialLinks = [
  { icon: Instagram, label: "Instagram", href: `https://instagram.com/${BRAND_HANDLE}` },
  { icon: Youtube, label: "YouTube", href: `https://www.youtube.com/@${BRAND_HANDLE}` },
];

const ContentCreator = () => {
  const seoConfig = {
    title: `${BRAND_PERSON_NAME} | Content Creator - Professional Profile`,
    description: "Professional content creator specializing in lifestyle, nutrition, beauty and travel content. Connect with authentic and inspiring content that resonates with thousands.",
    keywords: `${BRAND_PERSON_NAME}, content creator, professional profile, lifestyle content, nutrition, beauty, travel, influencer, social media, authentic content`,
    ogImage: `${BRAND_SITE_URL}/og.png`,
    canonical: `${BRAND_SITE_URL}/content-creator`,
    robots: "index, follow, max-snippet:-1, max-image-preview:large",
    type: "profile",
    author: BRAND_PERSON_NAME,
    modifiedTime: new Date().toISOString(),
    locale: "en_US",
    alternateLocale: "es_AR"
  };
  
  useSEO(seoConfig);
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-md mx-auto px-5 py-12 md:py-16">
        <header className="text-center mb-10 animate-fade-in">
          <div className="relative inline-block mb-8">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-border via-muted to-border opacity-60" />
            <div className="relative w-28 h-28 rounded-full overflow-hidden ring-2 ring-background shadow-lg">
              <img
                src={profileImage}
                alt={`${BRAND_PERSON_NAME} - Content Creator portrait photo`}
                loading="lazy"
                width={112}
                height={112}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-background/10 to-transparent pointer-events-none" />
          </div>

          <h1 className="text-3xl md:text-4xl font-display font-medium text-foreground tracking-wide mb-3 italic">
            Content Creator
          </h1>
          <div className="flex items-center justify-center gap-2 mb-5">
            <span className="w-6 h-px bg-border" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {BRAND_PERSON_NAME}
            </p>
            <span className="w-6 h-px bg-border" />
          </div>
        </header>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-4 text-center">
            <p className="text-sm leading-relaxed">
              {getBaseLanguage(language) === "es" 
                ? "Soy una joven <strong>creadora de contenido</strong>, modelo, deportista y estudiante de nutrición. Me encanta el lifestyle y comparto contenido auténtico sobre vida saludable, belleza y bienestar en Instagram, TikTok y YouTube."
                : "I'm a young <strong>content creator</strong>, model, athlete and nutrition student. I love lifestyle and share authentic content about healthy living, beauty and wellness on Instagram, TikTok and YouTube."
              }
            </p>
            <p className="text-sm leading-relaxed">
              {getBaseLanguage(language) === "es"
                ? "A través de mi contenido, combino mi pasión por el deporte, la nutrición y el estilo de vida saludable, inspirando a mi comunidad con momentos reales y consejos prácticos."
                : "Through my content, I combine my passion for sports, nutrition and healthy living, inspiring my community with real moments and practical tips."
              }
            </p>
          </div>
        </section>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <h2 className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground text-center mb-6 font-medium">
            Follow Me
          </h2>
          <div className="flex justify-center gap-6">
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 ui-transition-colors duration-200"
                aria-label={link.label}
              >
                <link.icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </section>

        <div className="flex items-center justify-center gap-3 mb-10">
          <span className="w-12 h-px bg-border" />
          <span className="w-1 h-1 bg-muted-foreground/40 rotate-45" />
          <span className="w-12 h-px bg-border" />
        </div>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.3s" }}>
          <h2 className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground text-center mb-6 font-medium">
            Topics
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {[
              { name: "Lifestyle", href: "/lifestyle" },
              { name: "Nutrition", href: "/nutricion" },
              { name: "Beauty", href: "/belleza" },
              { name: "Travel", href: "/viajes" },
            ].map((topic) => (
              <Link
                key={topic.name}
                to={topic.href}
                className="p-4 text-center border border-border rounded hover:border-foreground/30 ui-transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-foreground/20"
              >
                <span className="text-xs tracking-wide text-muted-foreground hover:text-foreground">
                  {topic.name}
                </span>
              </Link>
            ))}
          </div>
        </section>

        <div className="text-center animate-fade-in" style={{ animationDelay: "0.4s" }}>
          <Link
            to="/"
            onClick={() => {/* trackInternalLinkClick removed */}}
            className="inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-foreground/20 rounded"
          >
              Back to Home
          </Link>
        </div>

        <footer className="mt-14 text-center animate-fade-in" style={{ animationDelay: "0.5s" }}>
          <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/60">
            {`@${BRAND_HANDLE}`}
          </p>
        </footer>
      </main>
    </div>
  );
};

export default ContentCreator;


